import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  VStack,
  FormErrorMessage,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'; // Eye icons for password toggle
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import api from 'api/axiosConfig';
import API_BASE from 'configs/hostConfig';
import BackgroundWrapper from 'components/Background/Background';

const PasswordReset = () => {
  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required'),
  });

  const handleSubmit = (values, actions) => {
    console.log('Reset Password values:', values);
    const body = {
      token: token,
      password: values.password,
    };
    api
      .post(`${API_BASE}/api/auth/reset-password-via-email`, body)
      .then((res) => {
        if (res.status == 200) {
          navigate('/signin');
        }
      })
      .catch(() =>
        console.log('failed to update password with token: ', values.token)
      );
    actions.setSubmitting(false);
  };

  return (
    <BackgroundWrapper applyMd={true}>
      <Box
        bg="white"
        p={8}
        boxShadow="lg"
        borderRadius="lg"
        width="400px"
        maxWidth="90%"
        mx="auto"
        my={12}
      >
        <Heading as="h3" size="md" textAlign="center" mb={6}>
          Reset Password
        </Heading>

        <Formik
          initialValues={{ password: '', confirmPassword: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <VStack spacing={4}>
                {/* Password Field */}
                <Field name="password">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.password && form.touched.password}
                    >
                      <FormLabel>Password</FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                        />
                        <InputRightElement>
                          <IconButton
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            variant="ghost"
                            aria-label="Toggle Password Visibility"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.password}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Confirm Password Field */}
                <Field name="confirmPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.confirmPassword &&
                        form.touched.confirmPassword
                      }
                    >
                      <FormLabel>Confirm Password</FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                        />
                        <InputRightElement>
                          <IconButton
                            icon={
                              showConfirmPassword ? (
                                <ViewOffIcon />
                              ) : (
                                <ViewIcon />
                              )
                            }
                            variant="ghost"
                            aria-label="Toggle Confirm Password Visibility"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.confirmPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Reset Password Button */}
                <Button
                  colorScheme="green"
                  width="full"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Reset Password
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </Box>
    </BackgroundWrapper>
  );
};

export default PasswordReset;
